import { useAsyncStore } from '@/store/asyncStore';
import { ContentType, type CaptureEdgeKind } from '@vivport/backend/types';
import type { ObservationGroupInput } from '@/api/content-api';
import { useContentCreate } from './useContentQueries';

interface CreateRecordingOptions {
  /** The NeoChat this capture was submitted from; omitted for the manual door. */
  originProvenanceContentId?: number;
  linkToContentId?: number | null;
  observationGroup?: ObservationGroupInput | null;
}

interface UseRecordingCreateResult {
  createRecording: (
    file: File,
    options?: CreateRecordingOptions
  ) => Promise<{
    id: number;
    code: string;
    edgeKind: CaptureEdgeKind | null;
    observationGroupId: number | null;
  }>;
}

export function useRecordingCreate(): UseRecordingCreateResult {
  const createContentMutation = useContentCreate();
  const addTask = useAsyncStore((state) => state.addTask);

  const createRecording = async (file: File, options: CreateRecordingOptions = {}) => {
    const { linkToContentId, observationGroup, originProvenanceContentId } = options;
    const created = await createContentMutation.mutateAsync({
      contentType: ContentType.RECORDING,
      file,
      title: file.name,
      ...(originProvenanceContentId !== undefined ? { originProvenanceContentId } : {}),
      ...(linkToContentId ? { linkToContentId } : {}),
      ...(observationGroup ? { observationGroup } : {}),
    });
    addTask({
      title: `Processing ${file.name}`,
      description: 'Recording processing started.',
      status: 'processing',
      contentType: 'recording',
      contentId: created.id,
      payload: { recordingId: created.id },
    });
    return {
      id: created.id,
      code: created.code,
      edgeKind: created.edgeKind ?? null,
      observationGroupId: created.observationGroupId ?? null,
    };
  };

  return { createRecording };
}
